import { AxiosError } from 'axios';
import { useQuery, UseQueryOptions } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { FoldersResponse } from '@utils/types/folders/folder';
import { GET_URL_NEW } from '@utils/api/endpoints';
import { BaseError } from '@utils/types/shared/base';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';
import type { SortOrder } from '@utils/localStorage/sort-order-storage';

/** Merge UI search (folder name) with URL q; date filters (date_from/date_to) flow in q untouched */
function mergeQ(baseQ: string | undefined, folderNameQuery?: string) {
  const parts: string[] = [];
  if (baseQ && baseQ.trim()) parts.push(baseQ.trim());
  if (folderNameQuery && folderNameQuery.trim()) {
    parts.push(`name:ilike:%${folderNameQuery.trim()}%`);
  }
  return parts.join(',') || undefined;
}

/** Convert frontend sort order to backend sort_type parameter */
function getSortType(
  sortOrder?: SortOrder
): 'folders-first' | 'files-first' | undefined {
  if (sortOrder === 'folders-first') return 'folders-first';
  if (sortOrder === 'files-first') return 'files-first';
  return undefined;
}

export const useGetFolders = (
  opts?: {
    fetchAllFolders?: boolean;
    folderSearchQuery?: string;
    sortOrder?: SortOrder;
  } & UseQueryOptions<FoldersResponse, AxiosError<BaseError>, FoldersResponse>
) => {
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const rawSort = (searchParams.sort as string | undefined) || undefined;
  // Backend accepts: last_modified | created | name | type. The workspace
  // grid's "Uploaded" column is keyed on `date_upload` (a formatted display
  // field), so translate that alias to `created` before hitting the API.
  // Matches the same map in use-get-folder.tsx. Fixes #2035.
  const SORT_FIELD_ALIASES: Record<string, string> = {
    date_upload: 'created',
    uploaded: 'created'
  };
  const aliasedSort = rawSort
    ? (SORT_FIELD_ALIASES[rawSort] ?? rawSort)
    : undefined;
  const direction =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;

  const qFromUrl = (searchParams.q as string | undefined) || undefined;

  // Date range filters (from API changeset)
  const from = (searchParams.from as string | undefined) || undefined;
  const to = (searchParams.to as string | undefined) || undefined;

  const fetchOnlyFolders = opts?.fetchAllFolders === true;
  const folderSearchQuery = opts?.folderSearchQuery ?? '';
  const q = mergeQ(qFromUrl, folderSearchQuery);
  // sort=type passes through as-is now that the backend union query projects
  // 'caption' | 'transcript' | 'folder' for the type column (not just
  // 'document') — a real 3-way sort at the DB level (#2035). FE also re-sorts
  // within each page to handle the response's split folders/documents arrays.
  const sort = aliasedSort;
  const sortType = getSortType(opts?.sortOrder);

  const queryKey = [
    'folders',
    fetchOnlyFolders ? 'folders_only' : 'all',
    offset,
    limit,
    sort,
    direction,
    q,
    from,
    to,
    sortType
  ];

  return useQuery<FoldersResponse, AxiosError<BaseError>>(
    queryKey,
    async () => {
      const { folders } = GET_URL_NEW();
      const baseUrl = folders({ offset, limit, sort, direction, q, from, to });
      let url = fetchOnlyFolders ? `${baseUrl}&type=folders` : baseUrl;
      if (sortType) {
        url += `&sort_type=${sortType}`;
      }
      const { data } = await Axios.get<FoldersResponse>(url);
      return data;
    },
    {
      ...opts,
      keepPreviousData: true
    }
  );
};
